/* eslint @typescript-eslint/no-explicit-any: 0 */

/**
 * wrapEvents.ts
 * Clean application-level wrappers providing full tracking capabilities across:
 * - Product interaction (View, QuickView, Favorite)
 * - Cart lifecycle (Add, Remove, Replace, Clear, View Cart)
 * - Checkout / Purchase
 * - Identity / Web Events
 */

import {
  CATALOG_TYPES,
  PAGE_TYPES,
  PARTY_IDENTIFICATION_CONSTS,
  SUB_ACTIONS,
} from "./constants";
import { readAttribute } from "./helpers";
import { DATA_SELECTOR_ATTRS } from "./selectors";
import {
  sendCatalogEvent,
  sendCartItemEvent,
  sendReplaceCartEvent,
  sendOrderEvent,
  sendWebEvent,
  sendContactPointEmailEvent,
  sendContactPointPhoneEvent,
  sendContactPointAddressEvent,
  sendIdentityEvent,
  sendPartyIdentificationEvent,
  getDCIInstance,
} from "./sendEvents";

export interface UIProduct {
  id: string;
  name?: string;
  price?: number;
  wasPrice?: number;
  currency?: string;
  sku?: string;
  url?: string;
  imageUrl?: string;
  category?: string;
  categories?: string;
  primaryCategory?: string;
  relatedCategory?: string;
  subCategory?: string;
  subaction?: string;
  color?: string;
  colour?: string;
  size?: string;
  description?: string;
  stock?: number;
  store?: string;
  storeId?: string;
  candc?: string;
  collectionsFlags?: string;
  discountAmount?: number;
  discountType?: string;
  pageType?: string;
  type?: string;
  channel?: string;
}

export interface UICategory {
  id: string;
  name?: string;
  url?: string;
  pageType?: string;
  categories?: string;
  category?: string;
  primaryCategory?: string;
  subCategory?: string;
  relatedCategory?: string;
  candc?: string;
  collectionsFlags?: string;
  subaction?: string;
}

export interface UICollection {
  id: string;
  name?: string;
  url?: string;
  pageType?: string;
  categories?: string;
  category?: string;
  primaryCategory?: string;
  candc?: string;
  collectionsFlags?: string;
  subaction?: string;
}

export interface UICartItem {
  product: UIProduct;
  quantity: number;
}

export interface UIOrder {
  orderId: string;
  currency?: string;
  totalValue?: number;
  items: Array<{ product: UIProduct; quantity: number; price?: number }>;
}

export interface UIUserAddress {
  line1: string;
  line2?: string;
  line3?: string;
  line4?: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
  latitude?: number;
  longitude?: number;
}

export interface UIUserIdentity {
  userId?: string;
  email?: string;
  phoneNumber?: string;
  firstName?: string;
  lastName?: string;
  sfmcContactKey?: string;
  registrationId?: string;
  advertiserId?: string;
  isAnonymous?: boolean;
  addressLine1?: string;
  addressLine2?: string;
  addressLine3?: string;
  addressLine4?: string;
  city?: string;
  stateProvince?: string;
  postalCode?: string;
  country?: string;
  deviceType?: string;
  osName?: string;
  osVersion?: string;
  softwareApplicationName?: string;
  softwareApplicationVersion?: string;
}

type StoreDetails = Pick<UIProduct, "store" | "storeId">;

function resolveColour(product: UIProduct): string {
  return product.colour || product.color || "";
}

function resolveStoreDetails(): StoreDetails {
  const dci = getDCIInstance();

  if (dci?.cashDom) {
    const store = readAttribute(
      dci,
      `[${DATA_SELECTOR_ATTRS.STORE_NAME}]`,
      DATA_SELECTOR_ATTRS.STORE_NAME,
    );
    const storeId = readAttribute(
      dci,
      `[${DATA_SELECTOR_ATTRS.STORE_ID}]`,
      DATA_SELECTOR_ATTRS.STORE_ID,
    );

    if (store || storeId) {
      return {
        store: store || "",
        storeId: storeId || "",
      };
    }
  }

  if (typeof document !== "undefined") {
    const storeEl = document.querySelector(`[${DATA_SELECTOR_ATTRS.STORE_NAME}]`);
    const storeIdEl = document.querySelector(`[${DATA_SELECTOR_ATTRS.STORE_ID}]`);
    const store = storeEl?.getAttribute(DATA_SELECTOR_ATTRS.STORE_NAME) || "";
    const storeId = storeIdEl?.getAttribute(DATA_SELECTOR_ATTRS.STORE_ID) || "";

    if (store || storeId) {
      return { store, storeId };
    }
  }

  return {};
}

/**
 * Fields shared by every "catalog object" style event
 * (Product View, Quick View, Favorite).
 */
function mapCatalogFields(product: UIProduct, storeDetails: StoreDetails) {
  return {
    id: product.id,
    type: product.type || CATALOG_TYPES.PRODUCT,
    name: product.name || "",
    url: product.url || (global?.window as Window)?.location?.href || "",
    imageUrl: product.imageUrl || "",
    description: product.description || "",
    stock: product.stock,
    category: product.category || "",
    categories: product.categories || "",
    primaryCategory: product.primaryCategory || product.category || "",
    relatedCategory: product.relatedCategory || "",
    subCategory: product.subCategory || "",
    subaction: product.subaction || "",
    price: product.price,
    wasPrice:
      typeof product.wasPrice === "number" ? product.wasPrice : product.price,
    currency: product.currency || "",
    sku: product.sku || product.id,
    colour: resolveColour(product),
    size: product.size || "",
    store: product.store || storeDetails.store || "",
    storeId: product.storeId || storeDetails.storeId || "",
    candc: product.candc || "",
    collectionsFlags: product.collectionsFlags || "",
    discountAmount: product.discountAmount,
    discountType: product.discountType || "",
    pageType: product.pageType || "",
  };
}

/**
 * Fields shared by every "cart / order line item" style event
 * (Add To Cart, Remove From Cart, Replace Cart, Purchase items).
 */
function mapCartItemFields(product: UIProduct, storeDetails: StoreDetails) {
  return {
    catalogObjectId: product.id,
    catalogObjectType: product.type || CATALOG_TYPES.PRODUCT,
    price: product.price,
    wasPrice:
      typeof product.wasPrice === "number" ? product.wasPrice : product.price,
    currency: product.currency || "",
    name: product.name || "",
    url: product.url || "",
    imageUrl: product.imageUrl || "",
    description: product.description || "",
    sku: product.sku || product.id,
    colour: resolveColour(product),
    size: product.size || "",
    category: product.category || "",
    categories: product.categories || "",
    primaryCategory: product.primaryCategory || product.category || "",
    relatedCategory: product.relatedCategory || "",
    subCategory: product.subCategory || "",
    subaction: product.subaction || "",
    store: product.store || storeDetails.store || "",
    storeId: product.storeId || storeDetails.storeId || "",
    stock: product.stock,
    candc: product.candc || "",
    collectionsFlags: product.collectionsFlags || "",
    discountAmount: product.discountAmount,
    discountType: product.discountType || "",
    pageType: product.pageType || "",
    type: product.type || CATALOG_TYPES.PRODUCT,
  };
}

function normalizeQuantity(quantity: number): number {
  return quantity > 0 ? quantity : 1;
}

/**
 * 1. Product View (PDP)
 */
export function trackProductView(product: UIProduct): void {
  if (!product?.id) {
    return;
  }
  const dci = getDCIInstance();

  sendCatalogEvent({
    ...mapCatalogFields(product, resolveStoreDetails()),
    interactionName:
      dci?.CatalogObjectInteractionName?.ViewCatalogObject || "View Catalog Object",
  });
}

/**
 * 2. Quick View Product Modal
 */
export function trackQuickViewProduct(product: UIProduct): void {
  if (!product?.id) {
    return;
  }
  const dci = getDCIInstance();

  sendCatalogEvent({
    ...mapCatalogFields(product, resolveStoreDetails()),
    interactionName:
      dci?.CatalogObjectInteractionName?.QuickViewCatalogObject ||
      "Quick View Catalog Object",
  });
}

/**
 * 2a. Category View (PLP / CLP / GCP)
 */
export function trackCategoryView(category: UICategory): void {
  if (!category?.id) {
    return;
  }
  const dci = getDCIInstance();

  sendCatalogEvent({
    id: category.id,
    type: CATALOG_TYPES.CATEGORY,
    name: category.name || category.id,
    url: category.url || (global?.window as Window)?.location?.href || "",
    categories: category.categories || "",
    category: category.category || category.primaryCategory || "",
    primaryCategory: category.primaryCategory || category.category || "",
    subCategory: category.subCategory || "",
    relatedCategory: category.relatedCategory || "",
    pageType: category.pageType || PAGE_TYPES.PLP,
    candc: category.candc || "No",
    collectionsFlags: category.collectionsFlags || "-",
    subaction: category.subaction || SUB_ACTIONS.VIEWED,
    interactionName:
      dci?.CatalogObjectInteractionName?.ViewCatalogObject || "View Catalog Object",
  });
}

/**
 * 2b. Collection View (RBP)
 */
export function trackCollectionView(collection: UICollection): void {
  if (!collection?.id) {
    return;
  }
  const dci = getDCIInstance();

  sendCatalogEvent({
    id: collection.id,
    type: CATALOG_TYPES.COLLECTION,
    name: collection.name || collection.id,
    url: collection.url || (global?.window as Window)?.location?.href || "",
    categories: collection.categories || collection.name || "",
    category:
      collection.category || collection.primaryCategory || collection.name || "",
    primaryCategory:
      collection.primaryCategory || collection.category || collection.name || "",
    pageType: collection.pageType || PAGE_TYPES.RBP,
    candc: collection.candc || "No",
    collectionsFlags: collection.collectionsFlags || "-",
    subaction: collection.subaction || SUB_ACTIONS.VIEWED,
    interactionName:
      dci?.CatalogObjectInteractionName?.ViewCatalogObject || "View Catalog Object",
  });
}

/**
 * 3. Wishlist / Favorite Product
 */
export function trackFavoriteProduct(product: UIProduct): void {
  if (!product?.id) {
    return;
  }

  const subaction =
    product.subaction || (product as any).subAction || SUB_ACTIONS.ADDED;

  sendCatalogEvent({
    ...mapCatalogFields(product, resolveStoreDetails()),
    subaction,
    interactionName: "Favorite Catalog Object",
  });
}

/**
 * 4. Add To Cart
 */
export function trackAddToCart(
  cartId: string,
  product: UIProduct,
  quantity: number = 1,
  totalValue?: number,
): void {
  if (!product?.id || !cartId) {
    return;
  }
  const dci = getDCIInstance();

  sendCartItemEvent({
    cartEventId: cartId,
    ...mapCartItemFields(product, resolveStoreDetails()),
    quantity: normalizeQuantity(quantity),
    interactionName: dci?.CartInteractionName?.AddToCart || "Add To Cart",
    totalValue,
  });
}

/**
 * 5. Remove From Cart
 */
export function trackRemoveFromCart(
  cartId: string,
  product: UIProduct,
  quantity: number = 1,
  totalValue?: number,
): void {
  if (!product?.id || !cartId) {
    return;
  }
  const dci = getDCIInstance();

  sendCartItemEvent({
    cartEventId: cartId,
    ...mapCartItemFields(product, resolveStoreDetails()),
    quantity: normalizeQuantity(quantity),
    interactionName: dci?.CartInteractionName?.RemoveFromCart || "Remove From Cart",
    totalValue,
  });
}

/**
 * 6. Replace Cart (single item mutation)
 */
export function trackReplaceCart(cartId: string, cartItem?: UICartItem): void {
  if (!cartId || !cartItem?.product?.id) {
    return;
  }
  const dci = getDCIInstance();

  sendReplaceCartEvent({
    cartId,
    interactionName: dci?.CartInteractionName?.ReplaceCart || "Replace Cart",
    item: {
      cartEventId: cartId,
      ...mapCartItemFields(cartItem.product, resolveStoreDetails()),
      quantity: normalizeQuantity(cartItem.quantity),
    },
  });
}

/**
 * 7. Clear Cart
 */
export function trackClearCart(cartId: string): void {
  if (!cartId) {
    return;
  }
  const dci = getDCIInstance();

  sendReplaceCartEvent({
    cartId,
    interactionName: dci?.CartInteractionName?.ReplaceCart || "Replace Cart",
  });
}

/**
 * 8. Purchase — single order with one or more line items.
 *    The SDK creates one `order` event with all line items in the array.
 */
export function trackPurchase(order: UIOrder): void {
  if (!order?.orderId || !Array.isArray(order.items)) {
    return;
  }
  const dci = getDCIInstance();
  const interactionName = dci?.OrderInteractionName?.Purchase || "Purchase";
  const storeDetails = resolveStoreDetails();

  const items = order.items
    .filter(({ product }) => product?.id)
    .map(({ product, quantity, price }) => ({
      ...mapCartItemFields(product, storeDetails),
      price: price ?? product.price ?? 0,
      currency: order.currency || product.currency || "",
      quantity: normalizeQuantity(quantity),
    }));

  if (items.length === 0) {
    return;
  }

  const totalValue =
    order.totalValue ??
    items.reduce((sum, item) => sum + item.price * item.quantity, 0);

  sendOrderEvent({
    orderId: order.orderId,
    totalValue,
    currency: order.currency || items[0]?.currency || "",
    interactionName,
    items,
  });
}

/**
 * 9. Purchase — single item shorthand (backwards-compatible)
 */
export function trackPurchaseItem(
  orderId: string,
  product: UIProduct,
  quantity: number = 1,
): void {
  trackPurchase({
    orderId,
    currency: product.currency,
    items: [{ product, quantity }],
  });
}

/**
 * 10. Site Search
 */
export function trackSearch(searchTerm: string): void {
  if (!searchTerm?.trim()) {
    return;
  }

  sendWebEvent({
    searchTerm: searchTerm.trim(),
    action: "Search",
    interactionName: "Site Search",
  });
}

/**
 * 11. User Login / Identification
 */
export function trackUserLogin(identity: UIUserIdentity): void {
  if (!identity?.userId && !identity?.email) {
    return;
  }

  sendIdentityEvent({
    isAnonymous: identity.isAnonymous === false ? "false" : "true",
    email: identity.email || "",
    phoneNumber: identity.phoneNumber || "",
    sfmcContactKey: identity.sfmcContactKey || "",
    registrationId: identity.registrationId || "",
    advertiserId: identity.advertiserId || "",
    firstName: identity.firstName || "",
    lastName: identity.lastName || "",
    addressLine1: identity.addressLine1 || "",
    addressLine2: identity.addressLine2 || "",
    addressLine3: identity.addressLine3 || "",
    addressLine4: identity.addressLine4 || "",
    city: identity.city || "",
    stateProvince: identity.stateProvince || "",
    postalCode: identity.postalCode || "",
    country: identity.country || "",
    deviceType: identity.deviceType || "",
    osName: identity.osName || "",
    osVersion: identity.osVersion || "",
    softwareApplicationName: identity.softwareApplicationName || "",
    softwareApplicationVersion: identity.softwareApplicationVersion || "",
  });

  if (identity.userId) {
    sendPartyIdentificationEvent({
      userId: identity.userId,
      IDName: PARTY_IDENTIFICATION_CONSTS.ID_NAMES.PRIMARK_CUSTOMER,
      IDType: PARTY_IDENTIFICATION_CONSTS.ID_TYPES.CUSTOMER_ID,
    });
  }

  if (identity.email) {
    sendContactPointEmailEvent({
      email: identity.email.trim(),
    });
  }

  if (identity.phoneNumber) {
    sendContactPointPhoneEvent({
      phoneNumber: identity.phoneNumber.trim(),
    });
  }
}

/**
 * 12. Capture Email / Newsletter
 */
export function trackEmailCapture(
  email: string,
  latitude?: number,
  longitude?: number,
): void {
  if (!email?.trim()) {
    return;
  }

  sendContactPointEmailEvent({
    email: email.trim(),
    latitude,
    longitude,
  });
}

/**
 * 13. Capture Address
 */
export function trackUserAddress(address: UIUserAddress): void {
  if (
    !address?.line1 ||
    !address?.city ||
    !address?.country ||
    !address?.postalCode ||
    !address?.state
  ) {
    return;
  }

  sendContactPointAddressEvent({
    addressLine1: address.line1,
    addressLine2: address.line2 || "",
    addressLine3: address.line3 || "",
    addressLine4: address.line4 || "",
    city: address.city,
    stateProvince: address.state,
    postalCode: address.postalCode,
    country: address.country,
    latitude: address.latitude,
    longitude: address.longitude,
  });
}

/**
 * 14. Capture Phone Contact Info
 */
export function trackPhoneNumber(
  phone: string,
  latitude?: number,
  longitude?: number,
): void {
  if (!phone?.trim()) {
    return;
  }

  sendContactPointPhoneEvent({
    phoneNumber: phone.trim(),
    latitude,
    longitude,
  });
}
